export type ChartPoint = {
    x: number
    y: number
}

const round = (value: number): string => (Math.round(value * 100) / 100).toString()

/**
 * Smooth SVG path through the points using monotone cubic interpolation,
 * so curves never overshoot the data.
 */
export const smoothPath = (points: Array<ChartPoint>): string => {
    const count = points.length

    if (count === 0) return ''
    if (count === 1) return `M${round(points[0].x)},${round(points[0].y)}`

    const dx: Array<number> = []
    const slopes: Array<number> = []

    for (let index = 0; index < count - 1; index += 1) {
        dx[index] = points[index + 1].x - points[index].x || 1e-6
        slopes[index] = (points[index + 1].y - points[index].y) / dx[index]
    }

    const tangents: Array<number> = [slopes[0]]

    for (let index = 1; index < count - 1; index += 1) {
        const before = slopes[index - 1]
        const after = slopes[index]

        if (before * after <= 0) {
            tangents[index] = 0
        }
        else {
            tangents[index] = (3 * (dx[index - 1] + dx[index]))
                / ((2 * dx[index] + dx[index - 1]) / before + (dx[index] + 2 * dx[index - 1]) / after)
        }
    }

    tangents[count - 1] = slopes[count - 2]

    let path = `M${round(points[0].x)},${round(points[0].y)}`

    for (let index = 0; index < count - 1; index += 1) {
        const third = dx[index] / 3
        const start = points[index]
        const end = points[index + 1]

        path += ` C${round(start.x + third)},${round(start.y + third * tangents[index])}`
            + ` ${round(end.x - third)},${round(end.y - third * tangents[index + 1])}`
            + ` ${round(end.x)},${round(end.y)}`
    }

    return path
}

/** Closes a line path down to the baseline so it can be filled as an area. */
export const areaPath = (points: Array<ChartPoint>, baseline: number): string => {
    if (points.length === 0) return ''

    const first = points[0]
    const last = points[points.length - 1]

    return `${smoothPath(points)} L${round(last.x)},${round(baseline)} L${round(first.x)},${round(baseline)} Z`
}

interface ScaleOptions {
    width: number
    height: number
    padTop?: number
    padBottom?: number
    min?: number
    max?: number
    total?: number
    offset?: number
}

/** Maps a list of values onto chart coordinates. */
export const toPoints = (
    values: Array<number>,
    { width, height, padTop = 8, padBottom = 8, min, max, total, offset = 0 }: ScaleOptions
): Array<ChartPoint> => {
    const low = min ?? Math.min(...values)
    const high = max ?? Math.max(...values)
    const range = high - low || 1
    const slots = Math.max(1, (total ?? values.length) - 1)
    const usable = height - padTop - padBottom

    return values.map((value, index) => ({
        x: ((index + offset) / slots) * width,
        y: padTop + (1 - (value - low) / range) * usable
    }))
}

/** Sanitises a React id so it is safe inside an SVG url(#id) reference. */
export const svgId = (id: string): string => id.replace(/[^a-zA-Z0-9_-]/g, '')
